/**
 * Bản đồ nền thật (Leaflet + ô bản đồ OpenStreetMap) dùng chung cho khối "Bản đồ
 * số" ở MH-01 và trang Bản đồ thiết bị MH-05.
 *
 * Lớp nền đọc từ biến môi trường Vite `VITE_MAP_TILE_URL` và chú thích bản quyền
 * `VITE_MAP_ATTRIBUTION`; đổi sang nhà cung cấp bản đồ Việt Nam chỉ cần sửa hai
 * biến này, không sửa mã.
 *
 * Không dùng biểu tượng ảnh mặc định của Leaflet (đường dẫn ảnh hay vỡ khi đóng
 * gói). Điểm/nhóm toàn bộ đang kết nối vẽ HÌNH TRÒN xanh; điểm/nhóm còn thiết bị
 * mất kết nối vẽ Ô VUÔNG đỏ — hình dạng khác nhau nên màu không là phương tiện
 * duy nhất. Nhóm nhiều thiết bị hiện số thiết bị ngay trên điểm.
 *
 * Nội dung tooltip gắn bằng nút DOM với `textContent` (không bằng chuỗi HTML) vì
 * tên thiết bị là dữ liệu người dùng nhập.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ban-do-nen.css';

/** Ô bản đồ mặc định khi không cấu hình biến môi trường. */
export const URL_O_BAN_DO_MAC_DINH = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const CHU_THICH_BAN_DO_MAC_DINH = '&copy; OpenStreetMap contributors';

/** Tâm và mức thu phóng mặc định khi chưa có điểm nào (dùng chung với MH-05). */
export const TAM_BAN_DO_MAC_DINH = { tamViDo: 21.55, tamKinhDo: 105.82, zoom: 11 } as const;

export interface TamBanDo {
  tamViDo: number;
  tamKinhDo: number;
  zoom: number;
}

/** Khung nhìn thật của bản đồ sau mỗi lần di chuyển hoặc thu phóng. */
export interface KhungNhinBanDo extends TamBanDo {
  bacDo: number;
  namDo: number;
  dongDo: number;
  tayDo: number;
}

/** Một điểm vẽ trên bản đồ: một thiết bị (soThietBi = 1) hoặc một nhóm. */
export interface DiemBanDo {
  khoa: string;
  viDo: number;
  kinhDo: number;
  soThietBi: number;
  soMatKetNoi: number;
  /** Nội dung tooltip, giống `title` của bản SVG trước. */
  tieuDe: string;
  /**
   * Tooltip dạng thẻ thông tin (vd. thông tin xã ở MH-01): hàm dựng nút DOM, gọi khi tooltip
   * mở. Phải dựng bằng DOM/`textContent`, không bằng chuỗi HTML (tên địa bàn là dữ liệu).
   */
  theThongTin?: () => HTMLElement;
  onChon?: () => void;
}

export interface BanDoNenProps {
  diem: DiemBanDo[];
  /** Nhãn cho trình đọc màn hình. */
  nhan: string;
  /** Tự `fitBounds` theo các điểm mỗi khi danh sách điểm đổi. */
  tuCanKhung?: boolean;
  /** Khung nhìn điều khiển từ ngoài (nút di chuyển/thu phóng); bỏ trống = tự do. */
  khungNhin?: TamBanDo;
  onDoiKhungNhin?: (k: KhungNhinBanDo) => void;
  minZoom?: number;
  maxZoom?: number;
  className?: string;
}

/** Đọc cấu hình lớp nền tại thời điểm tạo bản đồ (kiểm thử đổi được bằng stubEnv). */
export function cauHinhLopNen(): { url: string; chuThich: string } {
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  return {
    url: env?.VITE_MAP_TILE_URL || URL_O_BAN_DO_MAC_DINH,
    chuThich: env?.VITE_MAP_ATTRIBUTION || CHU_THICH_BAN_DO_MAC_DINH,
  };
}

/** Bán kính điểm theo số thiết bị: nhóm lớn to hơn nhưng có trần. */
function banKinh(soThietBi: number): number {
  return Math.min(18, 6 + Math.log2(Math.max(1, soThietBi)) * 2.5);
}

function taoNutTooltip(noiDung: string): HTMLElement {
  const el = document.createElement('span');
  el.textContent = noiDung;
  return el;
}

function taoLop(d: DiemBanDo): L.Layer {
  const toaDo: L.LatLngTuple = [d.viDo, d.kinhDo];
  const r = banKinh(d.soThietBi);
  const so = d.soThietBi > 1 ? String(d.soThietBi) : '';
  let lop: L.Marker | L.CircleMarker;
  if (d.soMatKetNoi > 0) {
    const canh = Math.round(r * 2);
    lop = L.marker(toaDo, {
      icon: L.divIcon({
        className: 'ban-do-nen__diem ban-do-nen__diem--mat',
        html: so ? `<span>${so}</span>` : '',
        iconSize: [canh, canh],
      }),
      keyboard: false,
    });
  } else if (so) {
    const canh = Math.round(r * 2);
    lop = L.marker(toaDo, {
      icon: L.divIcon({
        className: 'ban-do-nen__diem ban-do-nen__diem--ket-noi',
        html: `<span>${so}</span>`,
        iconSize: [canh, canh],
      }),
      keyboard: false,
    });
  } else {
    lop = L.circleMarker(toaDo, {
      radius: r,
      className: 'ban-do-nen__tron',
      color: 'var(--card-bg)',
      weight: 1,
      fillColor: 'var(--online)',
      fillOpacity: 0.95,
    });
  }
  if (d.theThongTin) {
    const dung = d.theThongTin;
    lop.bindTooltip(() => dung(), {
      className: 'ban-do-nen__the',
      direction: 'auto',
      opacity: 1,
      sticky: false,
    });
  } else {
    lop.bindTooltip(taoNutTooltip(d.tieuDe));
  }
  if (d.onChon) {
    const chon = d.onChon;
    lop.on('click', () => chon());
  }
  return lop;
}

/** Kích thước khung chứa bằng 0 (jsdom, tab ẩn): bỏ qua phép tính theo điểm ảnh. */
function khongCoKichThuoc(el: HTMLElement): boolean {
  return el.clientWidth === 0 || el.clientHeight === 0;
}

export function BanDoNen({
  diem,
  nhan,
  tuCanKhung = false,
  khungNhin,
  onDoiKhungNhin,
  minZoom = 5,
  maxZoom = 18,
  className,
}: BanDoNenProps): ReactNode {
  const khungRef = useRef<HTMLDivElement | null>(null);
  const banDoRef = useRef<L.Map | null>(null);
  const lopDiemRef = useRef<L.LayerGroup | null>(null);
  const onDoiRef = useRef(onDoiKhungNhin);
  onDoiRef.current = onDoiKhungNhin;
  const tamBanDau = useRef<TamBanDo>(khungNhin ?? TAM_BAN_DO_MAC_DINH);

  // Tạo bản đồ một lần; gỡ đúng cách khi unmount.
  useEffect(() => {
    const el = khungRef.current;
    if (!el) return undefined;
    const { url, chuThich } = cauHinhLopNen();
    const tam = tamBanDau.current;
    const banDo = L.map(el, {
      center: [tam.tamViDo, tam.tamKinhDo],
      zoom: tam.zoom,
      minZoom,
      maxZoom,
    });
    L.tileLayer(url, { attribution: chuThich, maxZoom }).addTo(banDo);
    const lopDiem = L.layerGroup().addTo(banDo);
    banDo.on('moveend', () => {
      const c = banDo.getCenter();
      const b = banDo.getBounds();
      onDoiRef.current?.({
        tamViDo: c.lat,
        tamKinhDo: c.lng,
        zoom: banDo.getZoom(),
        bacDo: b.getNorth(),
        namDo: b.getSouth(),
        dongDo: b.getEast(),
        tayDo: b.getWest(),
      });
    });
    banDoRef.current = banDo;
    lopDiemRef.current = lopDiem;
    return () => {
      banDo.remove();
      banDoRef.current = null;
      lopDiemRef.current = null;
    };
    // minZoom/maxZoom chỉ đọc lúc tạo bản đồ.
  }, []);

  // Vẽ lại điểm khi dữ liệu đổi.
  useEffect(() => {
    const banDo = banDoRef.current;
    const lopDiem = lopDiemRef.current;
    const el = khungRef.current;
    if (!banDo || !lopDiem || !el) return;
    lopDiem.clearLayers();
    for (const d of diem) lopDiem.addLayer(taoLop(d));
    if (!tuCanKhung || khongCoKichThuoc(el)) return;
    if (diem.length > 0) {
      const bien = L.latLngBounds(diem.map((d): L.LatLngTuple => [d.viDo, d.kinhDo]));
      banDo.fitBounds(bien, { padding: [24, 24], maxZoom: 15 });
    } else {
      const t = TAM_BAN_DO_MAC_DINH;
      banDo.setView([t.tamViDo, t.tamKinhDo], t.zoom);
    }
  }, [diem, tuCanKhung]);

  // Khung nhìn điều khiển từ ngoài: chỉ đặt lại khi khác khung hiện tại, tránh
  // vòng lặp moveend → đổi khung nhìn → setView.
  const tamVi = khungNhin?.tamViDo;
  const tamKinh = khungNhin?.tamKinhDo;
  const zoom = khungNhin?.zoom;
  useEffect(() => {
    const banDo = banDoRef.current;
    if (!banDo || tamVi === undefined || tamKinh === undefined || zoom === undefined) return;
    const c = banDo.getCenter();
    const lech = Math.abs(c.lat - tamVi) > 1e-6 || Math.abs(c.lng - tamKinh) > 1e-6;
    if (lech || banDo.getZoom() !== zoom) banDo.setView([tamVi, tamKinh], zoom);
  }, [tamVi, tamKinh, zoom]);

  return (
    <div
      ref={khungRef}
      className={`ban-do-nen${className ? ` ${className}` : ''}`}
      role="region"
      aria-label={nhan}
      data-so-diem={diem.length}
    />
  );
}
